/**
 * Chat Panel - Claude Desktop Style
 *
 * Main chat interface with:
 * - Clean message layout (no avatars for assistant)
 * - Sparkle loading indicator
 * - Model selector in input area
 * - Artifact cards on last assistant message
 */

import * as React from 'react'
import type { Message } from 'ai'
import { ChatMessage } from './chat-message'
import { ChatInput } from './chat-input'
import { WelcomeMessage } from './welcome-message'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { AIModel } from './model-selector'
import type { ArtifactCardData } from './artifact-card'

interface ReasoningStep {
  type: string
  step?: string
  tools?: string[]
  count?: number
  elapsed?: string
}

interface ReasoningComplete {
  duration?: string
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
}

// Artifact type from route (to convert to ArtifactCardData)
interface Artifact {
  id: string
  type: 'chart' | 'table' | 'export'
  title: string
  data: unknown
  generatedAt: string
}

interface ChatPanelProps {
  messages: Message[]
  input: string
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onSubmit: (e: React.FormEvent) => void
  isLoading: boolean
  error?: Error
  streamData?: unknown[]
  // Model selector
  models?: AIModel[]
  selectedModel?: string | null
  onSelectModel?: (modelId: string) => void
  // Artifact handling
  artifacts?: Artifact[]
  onClickArtifact?: (artifact: ArtifactCardData) => void
  // Message actions
  onRetry?: () => void
  onStop?: () => void
  // Input state
  disabled?: boolean
}

// Sparkle Icon - like Claude's orange/coral thinking indicator
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function ChatPanel({
  messages,
  input,
  onInputChange,
  onSubmit,
  isLoading,
  error,
  streamData = [],
  models = [],
  selectedModel = null,
  onSelectModel,
  artifacts = [],
  onClickArtifact,
  onRetry,
  onStop,
  disabled = false,
}: ChatPanelProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading, artifacts.length])

  // Extract reasoning steps from stream data
  const reasoningSteps: ReasoningStep[] = []
  let reasoningComplete: ReasoningComplete | null = null

  for (const item of streamData) {
    if (typeof item === 'object' && item !== null) {
      const data = item as Record<string, unknown>
      if (data.type === 'reasoning-step') {
        reasoningSteps.push(data as unknown as ReasoningStep)
      } else if (data.type === 'reasoning-complete') {
        reasoningComplete = data as unknown as ReasoningComplete
      }
    }
  }

  // Convert artifacts to ArtifactCardData format for display in messages
  const artifactCards: ArtifactCardData[] = artifacts.map(a => ({
    id: a.id,
    type: a.type,
    title: a.title,
    subtitle: `${a.type === 'chart' ? 'Code' : a.type === 'table' ? 'Data' : 'Export'} · JSX`
  }))

  // Handle suggestion click
  const handleSuggestionClick = (question: string) => {
    // Create a synthetic event
    const syntheticEvent = {
      target: { value: question },
    } as React.ChangeEvent<HTMLTextAreaElement>
    onInputChange(syntheticEvent)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages Area */}
      <ScrollArea className="flex-1 px-2 sm:px-4">
        <div className="max-w-3xl mx-auto py-6">
          {messages.length === 0 ? (
            <WelcomeMessage onSuggestionClick={handleSuggestionClick} />
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => {
                const isLastAssistant =
                  message.role === 'assistant' &&
                  index === messages.length - 1

                // Skip rendering empty assistant message while loading (we show sparkle instead)
                if (isLastAssistant && isLoading && !message.content?.trim()) {
                  return null
                }

                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    reasoningSteps={isLastAssistant ? reasoningSteps : []}
                    reasoningDuration={
                      isLastAssistant ? reasoningComplete?.duration : undefined
                    }
                    tokenUsage={
                      isLastAssistant ? reasoningComplete?.usage : undefined
                    }
                    isStreaming={isLastAssistant && isLoading}
                    onRetry={message.role === 'assistant' && index === messages.length - 1 ? onRetry : undefined}
                    onClickArtifact={onClickArtifact}
                    artifacts={isLastAssistant ? artifactCards : []}
                  />
                )
              })}

              {/* Loading indicator - Claude style sparkle */}
              {isLoading && (() => {
                const lastMessage = messages[messages.length - 1]
                // Show loading if last message is user, or if assistant message has no content yet
                const showLoading = lastMessage?.role === 'user' ||
                  (lastMessage?.role === 'assistant' && !lastMessage.content?.trim())

                if (!showLoading) return null

                return (
                  <div className="flex items-center gap-2 py-2">
                    <SparkleIcon className="h-8 w-8 text-orange-500 animate-pulse" />
                  </div>
                )
              })()}

              {/* Error display */}
              {error && (
                <div className="p-4 bg-destructive/10 text-destructive rounded-xl border border-destructive/20">
                  <p className="font-medium">Error</p>
                  <p className="text-sm mt-1">{error.message}</p>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={scrollRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-2 sm:p-4 border-t border-border bg-background/50 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto">
          <ChatInput
            value={input}
            onChange={onInputChange}
            onSubmit={onSubmit}
            onStop={onStop}
            isLoading={isLoading}
            disabled={disabled}
            placeholder={disabled ? 'Loading...' : 'Reply...'}
            models={models}
            selectedModel={selectedModel}
            onSelectModel={onSelectModel}
          />
          {/* Disclaimer footer - Claude style */}
          <p className="mt-2 text-[11px] text-muted-foreground/70 text-center">
            Nurin is AI and can make mistakes. Please double-check responses.
          </p>
        </div>
      </div>
    </div>
  )
}
